import type { IPipelineStatusSummary, PipelineHealthLevel } from '@skystone/shared';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/format-relative-time';

type BadgeConfig = { dot: string; text: string; textColor: string };

const PIPELINE_STATUS_CONFIG: Record<PipelineHealthLevel, BadgeConfig> = {
  healthy: { dot: 'bg-success-500', text: 'Pipelines OK', textColor: 'text-success-600' },
  degraded: { dot: 'bg-warning-500', text: 'Pipeline stuck', textColor: 'text-warning-600' },
  error: { dot: 'bg-error-500', text: 'Pipeline failed', textColor: 'text-error-600' },
};

interface PipelineStatusBadgeProps {
  summary: IPipelineStatusSummary | null;
  isLoading: boolean;
}

export function PipelineStatusBadge({ summary, isLoading }: Readonly<PipelineStatusBadgeProps>) {
  if (isLoading || !summary) return null;

  const config = PIPELINE_STATUS_CONFIG[summary.health] ?? PIPELINE_STATUS_CONFIG.healthy;
  const timeLabel = summary.lastCompletedAt ? formatRelativeTime(summary.lastCompletedAt) : null;
  const failed = Array.isArray(summary.failedPipelines) ? summary.failedPipelines : [];
  const stuck = Array.isArray(summary.stuckPipelines) ? summary.stuckPipelines : [];
  const hasProblems = failed.length > 0 || stuck.length > 0;

  return (
    <div className="group relative inline-flex items-center shrink-0">
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label={timeLabel ? `${config.text}, last run ${timeLabel}` : config.text}
      >
        <span className={cn('h-2 w-2 rounded-full', config.dot)} />
        <span className={cn('font-medium', config.textColor)}>{config.text}</span>
      </button>
      <div className="invisible group-hover:visible group-focus-within:visible absolute right-0 top-full z-20 mt-1 min-w-52 rounded-md border bg-popover p-3 shadow-md">
        {timeLabel && (
          <div className="mb-1 text-xs text-muted-foreground">Last run {timeLabel}</div>
        )}
        {hasProblems ? (
          <>
            {failed.length > 0 && (
              <>
                <div className="mb-1 text-xs font-medium text-foreground">Failed pipelines</div>
                {failed.map((name) => (
                  <div key={`failed-${name}`} className="text-xs text-muted-foreground">{'✗'} {name}</div>
                ))}
              </>
            )}
            {stuck.length > 0 && (
              <>
                <div className="mb-1 mt-2 text-xs font-medium text-foreground">Stuck pipelines</div>
                {stuck.map((name) => (
                  <div key={`stuck-${name}`} className="text-xs text-muted-foreground">{'⚠'} {name}</div>
                ))}
              </>
            )}
          </>
        ) : (
          <div className="text-xs text-muted-foreground">All pipelines healthy</div>
        )}
      </div>
    </div>
  );
}
